import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import './styles/fonts.global.css';
import './styles/global.css';
import 'dayjs/locale/it';
import './i18n/config';

import { useEffect, useLayoutEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import { Center, Loader, MantineProvider } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';

import { useGetCurrentUserQuery } from '@api/auth.api';

import {
  selectIsLoadingAuth,
  setCredentials,
  stopLoadingAuth,
} from '@slices/auth.slice';

import useQueryParams from '@hooks/queryParams/useQueryParams';
import useAuth from '@hooks/useAuth';
import useCustomTranslations from '@hooks/useCustomTranslations';

import Cart from '@components/cart/Cart';
import LoginForm from '@components/LoginForm';
import ProductsSearch from '@components/productsSearch/ProductsSearch';
import RegisterForm from '@components/RegisterForm';

import ActivateUser from './pages/ActivateUser';
import PasswordReset from './pages/PasswordReset';
import PasswordResetRequest from './pages/PasswordResetRequest';
import UserAccount from './pages/userAccount/UserAccount';
import theme from './theme';

const elementorWidgetPage =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any)['react-pages-page'] || 'products-search';

export default function App() {
  const dispatch = useDispatch();
  const isLoading = useSelector(selectIsLoadingAuth);

  const { data: user, isLoading: isLoadingGetUser } = useGetCurrentUserQuery();
  const { isAuthenticated } = useAuth();

  const { init } = useCustomTranslations();

  // Init language
  useLayoutEffect(() => {
    init();
  }, []);

  // Get user when page is reloaded
  useEffect(() => {
    if (!isLoadingGetUser) {
      if (user) {
        dispatch(setCredentials(user));
      } else {
        dispatch(stopLoadingAuth());
      }

      // // Remove default elementor styling
      // const elementorClass = Array.from(document.body.classList).find((v) =>
      //   v.startsWith('elementor-kit-'),
      // );
      // if (elementorClass) {
      //   document.body.classList.remove(elementorClass);
      // }
    }
  }, [dispatch, user, isLoadingGetUser]);

  // --------------------------------------------------------------------------
  // Routing
  // --------------------------------------------------------------------------
  const [params, setParams] = useQueryParams();
  const page = useMemo(() => params.get('p'), [params]);

  return (
    <MantineProvider theme={theme} forceColorScheme="light">
      <Notifications autoClose={4000} />
      <DatesProvider settings={{ locale: 'it' }}>
        <ModalsProvider
          modalProps={{
            styles: { title: { fontSize: '1.2rem', fontWeight: 'bold' } },
          }}
        >
          {isLoading ? (
            <Center py="10rem">
              <Loader />
            </Center>
          ) : elementorWidgetPage === 'cart' ? (
            isAuthenticated ? (
              <Cart />
            ) : (
              <LoginForm />
            )
          ) : elementorWidgetPage === 'products-search' ? (
            <RouterProvider
              // TODO: dont use react-router
              router={createBrowserRouter([
                {
                  path: '*',
                  element: <ProductsSearch />,
                },
              ])}
            />
          ) : elementorWidgetPage === 'user' ? (
            isAuthenticated ? (
              <UserAccount />
            ) : page === 'activate-account' ? (
              <ActivateUser
                token={params.get('t') || ''}
                onSuccess={() => {
                  params.delete('p');
                  setParams(params);
                }}
              />
            ) : page === 'password-reset-request' ? (
              <PasswordResetRequest />
            ) : page === 'reset-password' ? (
              <PasswordReset />
            ) : page === 'register' ? (
              <RegisterForm />
            ) : (
              <LoginForm />
            )
          ) : (
            <></>
          )}
        </ModalsProvider>
      </DatesProvider>
    </MantineProvider>
  );
}
